import { Box, Typography, Stack, Skeleton } from '@mui/material';
import CustomTooltip from './CustomTooltip';

export interface CardProps {
  dateRange: string;
  title: React.ReactNode;
  /** Tooltip shown on hover over the title (metric definition) */
  titleTooltip?: React.ReactNode;
  mainContent?: React.ReactNode;
  topMetric?: string;
  loading?: boolean;
  subTitle?: string;
}

/** Card layout aligned with non-brand-overview: white container, header with border, footer "Last X weeks". */
const CARD_HEIGHT = 224;

export default function CiCard({ title, titleTooltip, dateRange, mainContent, topMetric, loading = false, subTitle }: CardProps) {
  return (
    <Box
      sx={{
        width: '100%',
        height: CARD_HEIGHT,
        maxHeight: CARD_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        bgcolor: 'white',
        border: '1px solid rgba(145, 158, 171, 0.12)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      }}
    >
      {/* Header: same as non-brand card-top-header (padding 12px) */}
      <Stack
        direction="row"
        spacing={3}
        justifyContent="space-between"
        alignItems="center"
        sx={{
          borderBottom: 1,
          borderColor: 'rgba(145, 158, 171, 0.2)',
          padding: '12px',
          flexShrink: 0,
        }}
      >
        {loading ? (
          <Skeleton variant="text" width={150} height={20} />
        ) : (
          <Stack direction="row" alignItems="center" sx={{ minWidth: 0 }} spacing={1}>
            {titleTooltip ? (
              <CustomTooltip title={titleTooltip} placement="top">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  {typeof title === 'string' ? (
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: '#333C47',
                        lineHeight: 1,
                        fontFamily: 'Public Sans, sans-serif',
                      }}
                    >
                      {title}
                    </Typography>
                  ) : (
                    <Box sx={{ fontSize: 14, fontWeight: 400, color: '#333C47', lineHeight: 1, fontFamily: 'Public Sans, sans-serif' }}>
                      {title}
                    </Box>
                  )}
                </Box>
              </CustomTooltip>
            ) : typeof title === 'string' ? (
              <Typography
                component="span"
                title={title}
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: '#333C47',
                  lineHeight: 1,
                  fontFamily: 'Public Sans, sans-serif',
                }}
              >
                {title}
              </Typography>
            ) : (
              <Box sx={{ fontSize: 14, fontWeight: 400, color: '#333C47', lineHeight: 1, fontFamily: 'Public Sans, sans-serif' }}>
                {title}
              </Box>
            )}
            {subTitle && (
              <Typography sx={{ fontSize: 14, fontWeight: 400, color: '#637381', fontFamily: 'Public Sans, sans-serif' }}>
                {subTitle}
              </Typography>
            )}
          </Stack>
        )}
        {loading ? (
          <Skeleton variant="text" width={48} height={20} />
        ) : (
          <Typography
            component="span"
            sx={{
              flexShrink: 0,
              fontSize: 14,
              fontWeight: 400,
              color: '#637381',
              lineHeight: 1,
              fontVariant: 'lining-nums, tabular-nums',
              fontFamily: 'Public Sans, sans-serif',
            }}
          >
            {topMetric}
          </Typography>
        )}
      </Stack>

      {/* Content: fixed height like non-brand, no scroll */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', pt: '16px', pb: '14px', px: 2, display: 'flex', flexDirection: 'column' }}>
        {loading ? (
          <Skeleton variant="rectangular" width="100%" height={120} sx={{ borderRadius: 1 }} />
        ) : (
          mainContent
        )}
      </Box>

      {/* Footer: same as non-brand card-footer */}
      <Stack
        sx={{
          padding: '8px 12px',
          backgroundColor: 'rgba(145, 158, 171, 0.08)',
          flexShrink: 0,
        }}
      >
        {loading ? (
          <Skeleton variant="text" width={100} height={13} />
        ) : (
          <Typography
            sx={{
              fontSize: 13,
              lineHeight: '13px',
              color: '#637381',
              fontFamily: 'Public Sans, sans-serif',
              fontWeight: 400,
            }}
          >
            Last {dateRange}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

